import { useEffect, useState } from 'react';

import { Constants } from '@constants';
import {
    TFormalOffer,
    TRBLSchedule
} from '@helpers/FormalOffers/formalOffers.types';
import { getIsRBLOffer } from '@helpers/FormalOffers/getIsRBLOffer';
import { isFormalOfferVariationType } from '@helpers/FormalOffers/isFormalOfferVariationType';
import { isInterestOnlyPlusOffer } from '@helpers/FormalOffers/isInterestOnlyPlusOffer';
import { UKOnly } from '@iwIntlProvider/CountryWrappers';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { OPEN_ERROR_MODAL } from '@iwModals/Modals.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconStarFilled } from '@iwoca/icons';
import { GetFormalOffersResponse } from '@iwoca/lapi-client/edge';
import { AlertBox, Button } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import IwMedia from '../../../../iwMedia';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { FooterSectionWrapper } from '../../../pageStructure/footerSectionWrapper/FooterSectionwraper';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper/index';
import { TrustPilot } from '../../../TrustPilot/TrustPilot';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals';
import { FormalOffer } from '../../MyOffers.types';
import EarlyRepaymentCalculator from '../earlyRepaymentCalculator/EarlyRepayment';
import { CustomiseOffer } from '../flexiApprovedView/CustomiseOffer/CustomiseOffer';
import messages from '../flexiApprovedView/CustomiseOffer/CustomiseOffer.messages';
import styles from '../flexiApprovedView/FlexiApprovedView.module.css';
import { LoanSummary } from '../flexiApprovedView/loanSummary/LoanSummary';
import { InlineEarlyRepaymentCalculator } from '../inlineEarlyRepaymentCalculator/InlineEarlyRepaymentCalculator';
import { OfferVariationModal } from '../offerVariationModal/OfferVariationModal';
import { RBLLoanSummary } from './RBLOfferConfigurationView/RBLLoanSummary/RBLLoanSummary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        amount,
        offerId,
        sweep
    }: {
        amount: number;
        offerId: FormalOffer['offer_id'];
        sweep?: number | null;
    }) => {
        let locationwithSearch: {
            pathname: string;
            offerId: string | undefined;
            amount: number;
            sweep?: number | undefined;
        } = { pathname: '/funding-requirements/', offerId, amount };
        if (sweep) {
            locationwithSearch = { ...locationwithSearch, sweep };
        }
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch(locationwithSearch)
        });
    },
    openErrorModal: () =>
        dispatch({ type: OPEN_ERROR_MODAL, id: 'FallbackErrorModal' })
});

type TOfferConfigurationViewProps = ReturnType<typeof mapDispatchToProps> & {
    maxDate: string;
    loanDuration: number;
    drawdownAmount: number;
    updateDrawdownAmount: (drawdownAmount: number) => void;
    selectedOffer: FormalOffer | null;
    updateSelectedOffer: (offer: FormalOffer | null) => void;
    formalOffers: NonNullable<GetFormalOffersResponse['data']>;
    interestOnlySplitTest: string;
    createCustomerOfferConfiguration: (options: {
        formalOffer: FormalOffer;
        sweep?: number;
        drawdownAmount: number;
        stateKey: string;
    }) => Promise<void>;
    rblSchedule: TRBLSchedule;
    updateRblSchedule: (next: TRBLSchedule) => void;
};

const OfferConfigurationView = ({
    maxDate,
    loanDuration,
    drawdownAmount,
    updateDrawdownAmount,
    selectedOffer,
    updateSelectedOffer,
    formalOffers,
    startDrawdown,
    openErrorModal,
    interestOnlySplitTest,
    createCustomerOfferConfiguration,
    rblSchedule,
    updateRblSchedule
}: TOfferConfigurationViewProps) => {
    const intl = useIntl();
    const amountInRange = selectedOffer?.available_cash! >= drawdownAmount;

    const [minimumDrawdownAmount, setMinimumDrawdownAmount] = useState<number>(
        Constants.MIN_DRAW_DOWN_AMOUNT
    );

    useEffect(() => {
        if (rblSchedule.schedule)
            setMinimumDrawdownAmount(
                rblSchedule.schedule?.data.summary.amount
                    .minimum_transaction_amount!
            );
    }, [rblSchedule]);

    const stateKey = useStateKey();

    const handleSubmit = async ({
        selectedOffer,
        sweep
    }: {
        selectedOffer: TFormalOffer;
        sweep?: number;
    }) => {
        if (selectedOffer.offer_id) {
            try {
                tracking.ampli.drawdownCtaClicked();
                await createCustomerOfferConfiguration({
                    formalOffer: selectedOffer,
                    sweep,
                    drawdownAmount,
                    stateKey
                });
                startDrawdown({
                    amount: drawdownAmount,
                    sweep: sweep ? sweep : null,
                    offerId: selectedOffer.offer_id
                });
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (err) {
                openErrorModal();
            }
        }
    };

    const [isVariationModalOpen, setIsVariationModalOpen] = useState(false);
    const variedOfferRelationship =
        formalOffers.formal_offer_variation_relationship_trees.find(
            (tree) =>
                tree.base_formal_offer_id === selectedOffer?.formal_offer_id
        )?.variations[0]; // For the interstatial modal, we assume that there's only at most one variation

    const variationType =
        !!variedOfferRelationship &&
        isFormalOfferVariationType(variedOfferRelationship.variation_type)
            ? variedOfferRelationship.variation_type
            : null;
    const variedOffer =
        formalOffers.formal_offers.find(
            (offer) =>
                offer.formal_offer_id ===
                variedOfferRelationship?.formal_offer_id
        ) ?? null;

    const shouldShowInterstitialPage =
        interestOnlySplitTest === 'INTERSTITIAL_PAGE';

    const onClickStartDrawdown = () => {
        if (variedOffer && variationType && shouldShowInterstitialPage) {
            setIsVariationModalOpen(true);
        } else {
            handleSubmit({
                selectedOffer: selectedOffer!,
                sweep:
                    rblSchedule.schedule?.data.summary.sweep
                        .selected_sweep_fraction || undefined
            });
        }
    };

    return (
        <>
            <section>
                <MainSectionWrapper
                    className={styles.mainSection}
                    innerWrapperClassName={styles.mainSectionRow}
                >
                    <div
                        className={styles.contentColumn}
                        data-testid="new-approval-view-left-column"
                    >
                        <CustomiseOffer
                            drawdownAmount={drawdownAmount}
                            updateDrawdownAmount={updateDrawdownAmount}
                            selectedOffer={selectedOffer}
                            updateSelectedOffer={updateSelectedOffer}
                            formalOffers={formalOffers}
                            updateRblSchedule={updateRblSchedule}
                            minimumDrawdownAmount={minimumDrawdownAmount}
                        />

                        {selectedOffer &&
                            isInterestOnlyPlusOffer(selectedOffer) && (
                                <AlertBox className="mt-m" variant="connect">
                                    <span className="block">
                                        <FormattedMessage
                                            id="_.offer_view.interest_only_plus_disclaimer_title"
                                            defaultMessage="How does it work?"
                                        />
                                    </span>
                                    <span className="font-reg">
                                        <FormattedMessage
                                            id="_.offer_view.interest_only_plus_disclaimer_text"
                                            defaultMessage="You will have full flexibility to repay early for free, and the ability to access additional funds at a later date too."
                                        />
                                    </span>
                                </AlertBox>
                            )}

                        {selectedOffer && !getIsRBLOffer(selectedOffer) && (
                            <IwMedia from="medium">
                                <InlineEarlyRepaymentCalculator
                                    drawdownAmount={drawdownAmount}
                                    offerId={selectedOffer?.offer_id!}
                                    productType={selectedOffer?.product_type!}
                                    loanDuration={loanDuration}
                                    maxDate={maxDate}
                                />
                            </IwMedia>
                        )}
                    </div>

                    <div
                        className={styles.contentColumn}
                        data-testid="new-approval-view-right-column"
                    >
                        <UKOnly>
                            {selectedOffer?.personal_guarantee_proportion ===
                                0 && (
                                <AlertBox className={styles.pgInfoBox}>
                                    <IconStarFilled
                                        fill="var(--color-success-drk)"
                                        height={20}
                                        width={20}
                                    />
                                    <FormattedMessage
                                        {...messages.personalGuaranteedNotRequired}
                                    />
                                </AlertBox>
                            )}
                        </UKOnly>
                        {selectedOffer && getIsRBLOffer(selectedOffer) ? (
                            <RBLLoanSummary
                                selectedOffer={selectedOffer}
                                schedule={rblSchedule?.schedule}
                                scheduleLoading={rblSchedule.scheduleLoading}
                                drawdownAmount={drawdownAmount}
                            />
                        ) : (
                            <LoanSummary
                                drawdownAmount={drawdownAmount}
                                selectedOffer={selectedOffer}
                            />
                        )}

                        <div className={styles.ctaContainer}>
                            <Button
                                variant="success"
                                data-testid="start-drawdown"
                                data-action-name="Approval continue button"
                                disabled={
                                    Boolean(!selectedOffer) || !amountInRange
                                }
                                onClick={onClickStartDrawdown}
                                className={styles.cta}
                            >
                                <FormattedMessage
                                    {...messages.cta}
                                    values={{
                                        amount: (
                                            <MoneyNoDecimals
                                                className={styles.summaryAmount}
                                                value={drawdownAmount}
                                            />
                                        )
                                    }}
                                />
                            </Button>

                            {selectedOffer &&
                                (amountInRange ? (
                                    <div className={styles.ctaHeplerText}>
                                        <FormattedMessage
                                            {...messages.CTAinfo}
                                        />
                                    </div>
                                ) : (
                                    <div className="mt-s text-s font-reg text-error">
                                        <FormattedMessage
                                            {...messages.CTAdisabled}
                                            values={{
                                                availableCash:
                                                    intlFormatCurrency({
                                                        intl,
                                                        value: selectedOffer.available_cash!
                                                    })
                                            }}
                                        />
                                    </div>
                                ))}
                        </div>
                    </div>

                    {/* currently using the old calculator layout for mobile */}
                    {selectedOffer && (
                        <IwMedia to="medium">
                            <div
                                className={styles.contentColumn_transparent}
                                data-testid="mobile-calculator"
                            >
                                <EarlyRepaymentCalculator
                                    drawdownAmount={drawdownAmount}
                                    offerId={selectedOffer.offer_id!}
                                    loanDuration={loanDuration}
                                    productType={selectedOffer.product_type!}
                                    maxDate={maxDate}
                                />
                            </div>
                        </IwMedia>
                    )}
                </MainSectionWrapper>
                <FooterSectionWrapper>
                    <UKOnly>
                        <TrustPilot />
                    </UKOnly>
                </FooterSectionWrapper>
            </section>
            {selectedOffer && variedOffer && variationType && (
                <OfferVariationModal
                    isOpen={isVariationModalOpen}
                    onClose={() => setIsVariationModalOpen(false)}
                    drawdownAmount={drawdownAmount}
                    baseOffer={selectedOffer}
                    variedOffer={variedOffer}
                    variationType={variationType}
                    handleSubmit={handleSubmit}
                />
            )}
        </>
    );
};

export const OfferConfigurationViewUI = connect(
    null,
    mapDispatchToProps
)(OfferConfigurationView);
